import { Transition } from 'react-transition-group';
import React, { useContext, useState, useCallback } from 'react';

import {
  CollapsibleMenu,
  CollapsibleSubMenu,
  CollapsibleSubMenuHeader,
  POPPER_TRANSITION_DURATION,
  PopperBase,
  SubMenuContent,
  SubMenuPopover,
  SubMenuPopoverContent,
  SubMenuPopoverLabel,
  SubMenuWrapper,
} from './styles';
import { SidebarContext } from 'components/Sidebar/Sidebar';
import PopoverBase from 'components/PopoverBase';
import Icon from 'components/Icon';
import SubMenuItem from './SubMenuItem';
import LocalIcon, { LocalIconTypes } from 'ui/LocalIcon';
import { black } from 'utils/style/colors';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { changeStateOFSideBar } from '../../store/app/appSlice';

const popperModifier = {
  preventOverflow: {
    boundariesElement: 'viewport',
  },
};

type OwnProps = {
  children: React.ReactNode;
  iconType: LocalIconTypes;
  initialOpenStatus: boolean;
  label: string;
};

type ComponentProps = OwnProps & React.HTMLAttributes<HTMLElement>;

type SubMenuType = {
  Item: typeof SubMenuItem;
} & React.FC<ComponentProps>;

const SubMenu: SubMenuType = ({
  children,
  iconType,
  initialOpenStatus = false,
  label,
  ...restProps
}: ComponentProps) => {
  const sideBarCollapsed = useContext(SidebarContext);
  const [open, setOpen] = useState(initialOpenStatus);
  const isMobile = useMobileDetection();
  const dispatch = useDispatch();
  const isSideBarCollapsed = useSelector(
    (state: RootState) => state.app.UI.sidebar.isSideBarCollapsed
  );

  const handleClick = () => {
    if (!sideBarCollapsed) {
      setOpen((preState) => !preState);
    }
  };
  const handleClickCallback = useCallback(handleClick, [sideBarCollapsed]);

  const handleChildClick = () => {
    if (isMobile && !isSideBarCollapsed) {
      dispatch(changeStateOFSideBar({ isCollapsed: true }));
    }
  };

  if (sideBarCollapsed) {
    return (
      <PopoverBase
        modifier={popperModifier}
        placement="left-start"
        trigger="hover"
        usePortal={true}
        target={
          <CollapsibleMenu>
            <LocalIcon iconName={iconType} color={`${black}`} type="sm" />
          </CollapsibleMenu>
        }
        childrenRenderFunc={({ placement, style, ref }) => (
          <Transition in={true} timeout={POPPER_TRANSITION_DURATION} appear>
            {(state) => (
              <PopperBase
                data-placement={placement}
                ref={ref}
                style={style}
                transiti
                onState={state}
              >
                <SubMenuPopover>
                  <SubMenuPopoverLabel>{label}</SubMenuPopoverLabel>
                  <SubMenuPopoverContent>{children}</SubMenuPopoverContent>
                </SubMenuPopover>
              </PopperBase>
            )}
          </Transition>
        )}
      />
    );
  }
  return (
    <SubMenuWrapper open={open}>
      <CollapsibleSubMenu
        onClick={handleClickCallback}
        open={open}
        {...restProps}
      >
        <CollapsibleSubMenuHeader>
          <LocalIcon iconName={iconType} color={`${black}`} type="sm" />
          <span>{label}</span>
        </CollapsibleSubMenuHeader>
        <Icon type={open ? 'chevron-up' : 'chevron-down'} />
      </CollapsibleSubMenu>
      {open && (
        <SubMenuContent>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                onClick: handleChildClick,
              } as any);
            }
            return child;
          })}
        </SubMenuContent>
      )}
    </SubMenuWrapper>
  );
};

SubMenu.Item = SubMenuItem;

export default SubMenu;
